import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Container, Form, Input, Label } from "reactstrap";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { useFormik } from "formik";
import { ToastContainer } from "react-toastify";
import { loginUser } from "../../store/actions";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import withRouter from "components/Common/withRouter";
import logo from "assets/images/logo-dark.png";
import authOverlay from "../../assets/images/bg-auth-overlay.png";
import { withTranslation } from "react-i18next";
import "./login.scss";

const Login = props => {
  const [passwordShow, setPasswordShow] = useState(false);

  document.title = "Flio.ai";
  const dispatch = useDispatch();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: ""
    },
    validationSchema: Yup.object({
      email: Yup.string().required(props.t("Email-Rq")),
      password: Yup.string().required(props.t("Password-Rq"))
    }),
    onSubmit: (values) => {
      dispatch(loginUser(values, props.router.navigate));
    }
  });

  const selectLoginState = (state) => state.Login;
  const LoginProperties = createSelector(
    selectLoginState,
    (login) => ({
      error: login.error
    })
  );

  const { error } = useSelector(LoginProperties);

  return (
    <div className="auth-page">
      <div className="background-animation">
        {/* Horizontal Roads */}
        <div className="road horizontal road-1">
          <div className="ball blue-1 move-right-fast" />
        </div>
        <div className="road horizontal road-2">
          <div className="ball blue-2 move-right-medium" />
        </div>
        <div className="road horizontal road-3">
          <div className="ball blue-3 move-right-slow" />
        </div>
        <div className="road horizontal road-4">
          <div className="ball blue-1 move-right-medium" />
        </div>

        {/* Vertical Roads */}
        <div className="road vertical road-1">
          <div className="ball blue-2 move-down-fast" />
        </div>
        <div className="road vertical road-2">
          <div className="ball blue-3 move-down-slow" />
        </div>
        <div className="road vertical road-3">
          <div className="ball blue-1 move-down-fast" />
        </div>

        {/* Diagonal Roads */}
        <div className="road diagonal road-1">
          <div className="ball blue-3 move-diagonal-fast" />
        </div>
        <div className="road diagonal road-2">
          <div className="ball blue-2 move-diagonal-medium" />
        </div>
        <div className="road diagonal road-3">
          <div className="ball blue-1 move-diagonal-slow" />
        </div>
      </div>

      <ToastContainer />
      <Container fluid className="auth-container p-0">
        <div className="auth-form-section">
          <div className="logo-wrapper">
            <Link to="/" className="d-block">
              <img src={logo} alt="Flio.ai" className="logo-dark-element" />
            </Link>
          </div>

          <h2 className="auth-title">{props.t("Login-Msg")}</h2>
          <p className="auth-subtitle">{props.t("Login-SubMsg")}</p>

          <Form className="auth-form"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <div className="mb-4">
              <Label className="form-label">{props.t("Email")}</Label>
              <Input
                name="email"
                placeholder={props.t("Email-Plc")}
                type="text"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.email || ""}
                className={
                  validation.touched.email && validation.errors.email
                    ? "is-invalid"
                    : ""
                }
              />
              {validation.touched.email && validation.errors.email && (
                <div className="invalid-feedback">
                  {validation.errors.email}
                </div>
              )}
            </div>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label className="form-label mb-0">{props.t("Password")}</Label>
                <Link to="/forgot-password" className="auth-links text-sm">
                  {props.t("Forgot-Password")}
                </Link>
              </div>
              <div className="auth-pass-inputgroup">
                <Input
                  name="password"
                  value={validation.values.password || ""}
                  type={passwordShow ? "text" : "password"}
                  placeholder={props.t("Password-Plc")}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  className={
                    validation.touched.password && validation.errors.password
                      ? "is-invalid"
                      : ""
                  }
                />
                <button 
                  onClick={() => setPasswordShow(!passwordShow)} 
                  className="btn btn-light"
                  type="button"
                >
                  <i className="mdi mdi-eye-outline"></i>
                </button>
              </div>
              {validation.touched.password && validation.errors.password && (
                <div className="invalid-feedback">
                  {validation.errors.password}
                </div>
              )}
            </div>

            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                id="auth-remember-check"
              />
              <Label
                className="form-check-label"
                htmlFor="auth-remember-check"
              >
                {props.t("Remember-Me")}
              </Label>
            </div>

            <button
              className="btn btn-primary w-100 mt-4"
              type="submit"
            >
              {props.t("Login")}
            </button>

            <div className="text-center mt-4">
              <p className="auth-links mb-0">
                {props.t("Dont-Have-Account")}{" "}
                <Link to="/register" className="fw-medium">
                  {props.t("Signup")}
                </Link>
              </p>
            </div>
          </Form>
        </div>
        <div className="auth-bg">
          <div 
            className="bg-overlay" 
            style={{ 
              background: `url(${authOverlay})`,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center"
            }}
          ></div>
        </div>
      </Container>
    </div>
  );
};

Login.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any
};

export default withRouter(withTranslation()(Login));
